import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BandReveal, InteractiveDots, Pressable, ScrollReveal, TextReveal } from '@/components/motion'
import type { IndustryData } from '@/data/industries'

export type CtaTone = 'purple' | 'pink' | 'black' | 'green' | 'gradient'

interface ToneStyle {
  band: string
  /** Text colour on the band. */
  ink: string
  /** Faded paragraph colour. */
  soft: string
  /** Solid button fill (spec: blue band → pink button, pink band → blue button). */
  solid: string
  /** Outline button. */
  outline: string
  dots: boolean
}

const PINK_BUTTON =
  'border-[#ff00f2] bg-[#ff00f2] text-white hover:text-[var(--color-landing-primary)] focus-visible:text-[var(--color-landing-primary)] dark:border-[#ff00f2] dark:bg-[#ff00f2] dark:text-white'
const BLUE_BUTTON =
  'border-[var(--color-landing-accent)] bg-[var(--color-landing-accent)] text-white hover:text-[var(--color-landing-primary)] focus-visible:text-[var(--color-landing-primary)] dark:border-[var(--color-landing-accent)] dark:bg-[var(--color-landing-accent)] dark:text-white'
const ON_COLOR_OUTLINE = 'landing-cta--on-color'

const TONE: Record<CtaTone, ToneStyle> = {
  purple: {
    band: 'bg-[var(--color-landing-secondary)]',
    ink: 'text-white',
    soft: 'text-white/85',
    solid: cn('landing-cta--on-color', BLUE_BUTTON),
    outline: ON_COLOR_OUTLINE,
    dots: true,
  },
  pink: {
    band: 'bg-[#ff00f2]',
    ink: 'text-white',
    soft: 'text-white/90',
    solid: cn('landing-cta--on-color', BLUE_BUTTON),
    outline: ON_COLOR_OUTLINE,
    dots: true,
  },
  black: {
    band: 'bg-[var(--color-landing-primary)] dark:bg-black',
    ink: 'text-white',
    soft: 'text-white/70',
    solid: cn('landing-cta--on-color', PINK_BUTTON),
    outline: ON_COLOR_OUTLINE,
    dots: true,
  },
  gradient: {
    band: 'bg-[linear-gradient(105deg,var(--color-landing-accent)_0%,var(--color-landing-secondary)_100%)]',
    ink: 'text-white',
    soft: 'text-white/85',
    solid: cn('landing-cta--on-color', PINK_BUTTON),
    outline: ON_COLOR_OUTLINE,
    dots: true,
  },
  // Green carries black type: white on #00d722 fails contrast.
  green: {
    band: 'bg-[#00d722]',
    ink: 'text-[#0a0a0a]',
    soft: 'text-[#0a0a0a]/75',
    solid:
      'border-[#0a0a0a] bg-[#0a0a0a] text-white [--sweep-color:#fff] hover:text-[#0a0a0a] focus-visible:text-[#0a0a0a] dark:border-[#0a0a0a] dark:bg-[#0a0a0a] dark:text-white dark:hover:text-[#0a0a0a]',
    outline:
      'border-[#0a0a0a] text-[#0a0a0a] [--sweep-color:#0a0a0a] hover:text-white focus-visible:text-white dark:border-[#0a0a0a] dark:text-[#0a0a0a] dark:hover:text-white',
    dots: true,
  },
}

/**
 * Closing CTA band for the industry layouts (spec: full-bleed colour band,
 * h1-size heading, then the actions and a 45ch paragraph in one row). Each
 * layout passes its own tone so the six pages end on different colours.
 */
export function IndustryCtaBand({ industry, tone }: { industry: IndustryData; tone: CtaTone }) {
  const t = TONE[tone]
  return (
    <BandReveal>
      <section className={cn('landing-section-lg relative overflow-hidden', t.band)}>
        {t.dots && <InteractiveDots />}
        <div className="landing-container relative z-10">
          <TextReveal as="h2" className={cn('landing-text-hero max-w-[20ch]', t.ink)}>
            {industry.ctaHeadline}
          </TextReveal>
          <ScrollReveal direction="none" delay={0.2}>
            <div className="mt-12 flex flex-col gap-8 md:mt-16 lg:flex-row lg:items-center lg:gap-16">
              <div className="flex flex-wrap items-center gap-4">
                <Pressable className="inline-block">
                  <Link to="/contact" className={cn('landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep', t.solid)}>
                    Book a demo
                    <ArrowRight aria-hidden className="h-5 w-5" />
                  </Link>
                </Pressable>
                <Link to="/register" className={cn('landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep', t.outline)}>
                  Start free trial
                </Link>
              </div>
              <p className={cn('landing-text-body-lg max-w-[45ch]', t.soft)}>{industry.ctaDescription}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </BandReveal>
  )
}
